import {
  decodeSerializedTransactionBytes,
  deserializeMintTransactionFromBackend,
} from "./tokenCreatorHelpers";
import { RAW_TX_SEND_OPTS } from "../constants";

function hasNonZeroSignature(sig) {
  if (!sig || sig.length === 0) return false;
  return sig.some((b) => b !== 0);
}

function ensureWalletSignature(transaction) {
  if (!transaction?.signatures?.length) {
    throw new Error("Wallet signature is missing from signed transaction.");
  }
  if (!hasNonZeroSignature(transaction.signatures[0])) {
    throw new Error("Wallet signature is missing from signed transaction.");
  }
}

function toBase64(serializedTx) {
  return Buffer.from(serializedTx).toString("base64");
}

function deserializeSerializedTx(rawSerializedTx) {
  if (typeof rawSerializedTx !== "string") {
    throw new Error("Invalid serialized transaction from backend.");
  }

  const txBuffer = decodeSerializedTransactionBytes(rawSerializedTx);
  return deserializeMintTransactionFromBackend(txBuffer);
}

export function buildMintOptionsFromUi({ toggles, form }) {
  return {
    revokeMintAuthority: toggles.revokeMint,
    revokeFreezeAuthority: toggles.revokeFreeze,
    immutableMetadata: toggles.revokeUpdate,
    multiWallet: toggles.multiWallet,
    distribution: toggles.multiWallet ? form.distribution : null,
    vanityPrefix:
      toggles.vanityAddress && toggles.vanityUsePrefix ? form.prefix : null,
    vanitySuffix:
      toggles.vanityAddress && toggles.vanityUseSuffix ? form.suffix : null,
    freezeAuthorityEnabled: true,
  };
}

export function isRetryableMintSignError(error) {
  const code = error?.data?.code || error?.response?.data?.code;
  if (code === "MINT_SIGN_JOB_NOT_FOUND" || code === "MINT_SIGN_SESSION_EXPIRED") {
    return true;
  }
  const status = error?.status ?? error?.response?.status;
  const message = String(
    error?.message || error?.data?.error || error?.response?.data?.error || "",
  ).toLowerCase();
  if (status === 404 && message.includes("not found or expired")) return true;
  if (message.includes("signing session expired")) return true;
  return (
    message.includes("blockhash not found") ||
    message.includes("block height exceeded") ||
    message.includes("expired blockhash")
  );
}

async function runSplMintSigningAttempt({
  apiService,
  connection,
  walletAddress,
  walletSignTransaction,
  buildUnsignedPayload,
  requestedJobId,
}) {
  if (!walletSignTransaction) {
    throw new Error(
      "Wallet signTransaction is not available. Reconnect your wallet and try again.",
    );
  }

  const buildUnsignedRes = await apiService.buildUnsignedMintTransaction(
    buildUnsignedPayload,
  );
  const unsignedData = buildUnsignedRes?.data;
  if (!unsignedData?.serializedUnsignedTx) {
    throw new Error(
      "Failed to build unsigned transaction - missing serializedUnsignedTx.",
    );
  }

  const finalizeJobId = unsignedData.jobId ?? requestedJobId;
  if (!finalizeJobId) {
    throw new Error("Missing signing job id for finalize-sign step.");
  }

  const unsignedTx = deserializeSerializedTx(unsignedData.serializedUnsignedTx);
  const walletSignedTx = await walletSignTransaction(unsignedTx);
  ensureWalletSignature(walletSignedTx);

  const walletSignedSerializedTx = toBase64(walletSignedTx.serialize());
  const finalizeRes = await apiService.finalizeSignMintTransaction({
    jobId: finalizeJobId,
    walletSignedSerializedTx,
    broadcast: false,
    payerPublicKey: walletAddress,
  });

  const finalizeData = finalizeRes?.data;
  let signature = finalizeData?.signature || null;

  if (!signature) {
    if (!finalizeData?.serializedFullySignedTx) {
      throw new Error(
        "Finalize sign failed - missing serializedFullySignedTx from backend.",
      );
    }

    const fullySignedTx = deserializeSerializedTx(
      finalizeData.serializedFullySignedTx,
    );

    signature = await connection.sendRawTransaction(
      fullySignedTx.serialize(),
      RAW_TX_SEND_OPTS,
    );
  }

  return {
    signature,
    mintPublicKey: unsignedData.mintPublicKey || finalizeData?.mintPublicKey,
  };
}

export async function runSplMintTwoPhaseSigningFlow(params) {
  try {
    return await runSplMintSigningAttempt(params);
  } catch (error) {
    if (!isRetryableMintSignError(error)) throw error;
    params.onSignSessionRetry?.(error);
    return runSplMintSigningAttempt(params);
  }
}
